import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { useSite } from '../context/SiteContext';
import SEO from '../components/SEO';
import {
  IconMap, IconUsers, IconImage, IconSettings,
  IconLogout, IconPlus, IconEdit, IconTrash, IconEye
} from '../components/Icons';
import styles from './AdminDashboard.module.css';
import ImageFrameEditor from '../components/ImageFrameEditor';
import { normalizeTripImages } from '../data/siteData';

// ─── GUARD ────────────────────────────────────────────────────────────────────
function useAdminGuard() {
  const navigate = useNavigate();
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) navigate('/admin-login');
    });
    return unsub;
  }, [navigate]);
}

// ─── TRIP MODAL ───────────────────────────────────────────────────────────────
const defaultTripForm = {
  title: '', titleAr: '', location: '', distance: '5 KM',
  time: '2:00 PM', level: 'Moderate', fees: '25 JDs',
  date: '', category: 'hiking', thisWeek: false,
  description: '', highlights: '', included: '',
  images: [{ url: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&q=80', isMain: true, fitMode: 'cover', focalY: 50 }],
};

function TripModal({ trip, onSave, onClose, categories }) {
  const [form, setForm] = useState(() => trip
    ? { ...trip, images: normalizeTripImages(trip.images) }
    : defaultTripForm);
  const [isDirty, setIsDirty] = useState(false);
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setIsDirty(true); };

  const handleClose = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) return;
    onClose();
  };

  // ── Images helpers ──
  const addImage = () => set('images', [...form.images, { url: '', isMain: false, fitMode: 'cover', focalY: 50 }]);
  const removeImage = (idx) => {
    const updated = form.images.filter((_, i) => i !== idx);
    // If we removed the main image, make the first one main
    if (form.images[idx].isMain && updated.length > 0) {
      updated[0] = { ...updated[0], isMain: true };
    }
    set('images', updated);
  };
  const updateImageUrl = (idx, url) =>
    set('images', form.images.map((img, i) => i === idx ? { ...img, url } : img));
  const updateImageMeta = (idx, meta) =>
    set('images', form.images.map((img, i) => i === idx ? { ...img, ...meta } : img));
  const setMainImage = (idx) =>
    set('images', form.images.map((img, i) => ({ ...img, isMain: i === idx })));

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{trip ? 'Edit Trip' : 'Add New Trip'}</h2>
          <button className={styles.modalClose} onClick={handleClose}>✕</button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.formGrid}>
            <div className={`${styles.formGroup} ${styles.spanFull}`}>
              <label className={styles.label}>Trip Title (English) *</label>
              <input className={styles.input} value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Ramadan Hike – Zubia Forest" />
            </div>
            <div className={`${styles.formGroup} ${styles.spanFull}`}>
              <label className={styles.label}>Trip Title (Arabic)</label>
              <input className={styles.input} value={form.titleAr} onChange={e => set('titleAr', e.target.value)} placeholder="العنوان بالعربية" dir="rtl" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Location</label>
              <input className={styles.input} value={form.location} onChange={e => set('location', e.target.value)} placeholder="Ajloun" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Date</label>
              <input className={styles.input} value={form.date} onChange={e => set('date', e.target.value)} placeholder="Friday 13/03" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Time</label>
              <input className={styles.input} value={form.time} onChange={e => set('time', e.target.value)} placeholder="2:00 PM" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Distance</label>
              <input className={styles.input} value={form.distance} onChange={e => set('distance', e.target.value)} placeholder="5 KM" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Fees (JDs)</label>
              <input className={styles.input} value={form.fees} onChange={e => set('fees', e.target.value)} placeholder="25 JDs" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Category</label>
              <select className={styles.select} value={form.category} onChange={e => set('category', e.target.value)}>
                {(categories || []).map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Difficulty Level</label>
              <select className={styles.select} value={form.level} onChange={e => set('level', e.target.value)}>
                {['Easy', 'Easy to Moderate', 'Moderate', 'Moderate to Hard', 'Hard', 'All Levels'].map(l => (
                  <option key={l}>{l}</option>
                ))}
              </select>
            </div>

            {/* ── Images Manager ── */}
            <div className={`${styles.formGroup} ${styles.spanFull}`}>
              <div className={styles.imagesHeader}>
                <label className={styles.label}>Trip Photos</label>
                <button type="button" className={styles.addImgBtn} onClick={addImage}>
                  <IconPlus size={13} /> Add Photo
                </button>
              </div>
              <p className={styles.imageHint}>Recommended: landscape images ~1600×900 for best hero display.</p>
              <div className={styles.imagesList}>
                {form.images.map((img, idx) => (
                  <div key={idx} className={`${styles.imageRow} ${img.isMain ? styles.imageRowMain : ''}`}>
                    <div className={styles.imageRowTop}>
                      <input
                        className={styles.input}
                        placeholder="https://images.unsplash.com/..."
                        value={img.url}
                        onChange={e => updateImageUrl(idx, e.target.value)}
                      />
                      <div className={styles.imageRowActions}>
                        <button
                          type="button"
                          className={`${styles.setMainBtn} ${img.isMain ? styles.setMainBtnActive : ''}`}
                          onClick={() => setMainImage(idx)}
                          title="Set as main image"
                        >
                          {img.isMain ? '★ Main' : '☆ Set Main'}
                        </button>
                        {form.images.length > 1 && (
                          <button type="button" className={styles.removeImgBtn} onClick={() => removeImage(idx)}>✕</button>
                        )}
                      </div>
                    </div>
                    {img.url && (
                      <ImageFrameEditor
                        url={img.url}
                        fitMode={img.fitMode}
                        focalY={img.focalY}
                        onChange={(meta) => updateImageMeta(idx, meta)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className={`${styles.formGroup} ${styles.spanFull}`}>
              <label className={styles.label}>Description</label>
              <textarea className={styles.textarea} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe this trip..." />
            </div>

            <div className={`${styles.formGroup} ${styles.spanFull}`}>
              <label className={styles.label}>Highlights <span style={{ fontWeight: 400, color: '#999' }}>(one per line)</span></label>
              <textarea
                className={styles.textarea}
                value={Array.isArray(form.highlights) ? form.highlights.join('\n') : (form.highlights || '')}
                onChange={e => set('highlights', e.target.value.split('\n').filter(l => l.trim()))}
                placeholder={`Wander through thick forests filled with pine and oak trees\nExperience peaceful nature trails away from the city hustle`}
                rows={4}
              />
            </div>

            <div className={`${styles.formGroup} ${styles.spanFull}`}>
              <label className={styles.label}>Included</label>
              <input
                className={styles.input}
                value={form.included || ''}
                onChange={e => set('included', e.target.value)}
                placeholder="e.g. Transportation, breakfast, snacks, lunch, water, and guide"
              />
            </div>
            <div className={`${styles.checkGroup} ${styles.spanFull}`}>
              <input type="checkbox" id="thisWeek" checked={!!form.thisWeek} onChange={e => set('thisWeek', e.target.checked)} />
              <label htmlFor="thisWeek">🗓 This Week's Trip <span className={styles.checkHint}>(shows on homepage)</span></label>
            </div>
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={handleClose}>Cancel</button>
          <button className={styles.saveBtn} onClick={() => { if (form.title) onSave(form); }}>
            {trip ? 'Save Changes' : 'Add Trip'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
const showSeedButton = process.env.REACT_APP_SHOW_SEED === 'true';

export default function AdminDashboard() {
  useAdminGuard();
  const navigate = useNavigate();
  const { data, addTrip, updateTrip, softDeleteTrip, restoreTrip, deleteTrip, updateCategories, updateCorporate, updateAbout, updateHero, updateContact, updatePrivacyPolicy, addPost, updatePost, deletePost, addEvent, updateEvent, deleteEvent, runSeed } = useSite();
  const [seeding, setSeeding] = useState(false);

  const handleSeed = async () => {
    if (!window.confirm('This will upload all default data to Firestore. Run only once. Continue?')) return;
    setSeeding(true);
    await runSeed();
    setSeeding(false);
    showSaved('✅ Firestore seeded!');
  };

  const [tab, setTab] = useState('overview');
  const [tripModal, setTripModal] = useState(null); // null | 'new' | trip object
  const [aboutForm, setAboutForm] = useState(data.about);
  const [heroForm, setHeroForm] = useState(data.hero);
  const [contactForm, setContactForm] = useState(data.contact);
  const [catForm, setCatForm] = useState({ label: '', color: '#2d7a3e', bg: '#e6f4ea' });
  const [editingCat, setEditingCat] = useState(null); // null | category id
  const [corpForm, setCorpForm] = useState(data.corporate);
  const [editingService, setEditingService] = useState(null); // null | service id
  const [serviceForm, setServiceForm] = useState({ icon: '', title: '', body: '' });
  const [newClient, setNewClient] = useState({ name: '', logo: '' });
  const [corpAccordion, setCorpAccordion] = useState('content'); // content | services | packages | clients
  const [privacyForm, setPrivacyForm] = useState(data.privacyPolicy);
  const [blogModal, setBlogModal] = useState(null); // null | 'new' | post object
  const emptyPost = { title: '', slug: '', category: 'trip-report', coverImage: '', date: '', author: 'Keepers Team', excerpt: '', published: true, sections: [{ id: 1, heading: '', body: '' }] };
  const [blogForm, setBlogForm] = useState(emptyPost);
  const [blogDirty, setBlogDirty] = useState(false);
  const [eventModal, setEventModal] = useState(null); // null | 'new' | event object
  const emptyEvent = { title: '', location: '', date: '', time: '', fees: '', groupSize: '', image: '', description: '', tags: '', upcoming: false, published: true };
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [eventDirty, setEventDirty] = useState(false);

  const patchBlogForm = (updater) => {
    setBlogForm(updater);
    setBlogDirty(true);
  };

  const handleCloseBlog = () => {
    if (blogDirty && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) return;
    setBlogModal(null);
    setBlogDirty(false);
  };

  const patchEventForm = (updater) => {
    setEventForm(updater);
    setEventDirty(true);
  };

  const handleCloseEvent = () => {
    if (eventDirty && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) return;
    setEventModal(null);
    setEventDirty(false);
  };
  const [editingPkg, setEditingPkg] = useState(null);
  const [pkgForm, setPkgForm] = useState({ title: '', destination: '', employees: '', price: '', details: '', image: '' });
  const [saved, setSaved] = useState('');

  const showSaved = (msg) => { setSaved(msg); setTimeout(() => setSaved(''), 3000); };

  const handleSaveTrip = async (trip) => {
    const payload = {
      ...trip,
      images: normalizeTripImages(trip.images),
    };
    if (payload.id) await updateTrip(payload);
    else await addTrip(payload);
    setTripModal(null);
    showSaved('✅ Trip saved!');
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  const navItems = [
    { id: 'overview',    label: 'Overview',         icon: <IconUsers size={18} /> },
    { id: 'trips',       label: 'Trips',            icon: <IconMap size={18} /> },
    { id: 'categories',  label: 'Categories',       icon: <IconSettings size={18} /> },
    { id: 'corporate',   label: 'Corporate',         icon: <IconUsers size={18} /> },
    { id: 'about',       label: 'About',            icon: <IconUsers size={18} /> },
    { id: 'hero',        label: 'Hero Section',     icon: <IconSettings size={18} /> },
    { id: 'contact',     label: 'Contact & Social', icon: <IconSettings size={18} /> },
    { id: 'privacy',     label: 'Privacy Policy',   icon: <IconSettings size={18} /> },
    { id: 'blog',        label: 'Blog',             icon: <IconImage size={18} /> },
    { id: 'events',      label: 'Experiences',      icon: <IconMap size={18} /> },
  ];

  return (
    <>
      <SEO
        title="Admin Dashboard | Keepers Experience"
        description="Admin panel"
        noindex={true}
      />
      <div className={styles.layout}>

      {/* SIDEBAR */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarLogo}>
          <div>
            <div className={styles.sidebarLogoText}>Keepers</div>
            <div className={styles.sidebarLogoSub}>Admin Dashboard</div>
          </div>
        </div>

        <nav className={styles.sidebarNav}>
          {navItems.map(item => (
            <button
              key={item.id}
              className={`${styles.navItem} ${tab === item.id ? styles.navActive : ''}`}
              onClick={() => setTab(item.id)}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className={styles.sidebarBottom}>
          <button className={styles.viewSiteBtn} onClick={() => navigate('/')}>
            <IconEye size={16} /> View Website
          </button>
          <button className={styles.logoutBtn} onClick={handleLogout}>
            <IconLogout size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main className={styles.main}>
        {saved && <div className={styles.savedToast}>{saved}</div>}

        {/* ── OVERVIEW ── */}
        {tab === 'overview' && (
          <div className={styles.tabContent}>
            <h1 className={styles.pageTitle}>Overview</h1>
            <p className={styles.pageSub}>Welcome to the Keepers Experience admin panel.</p>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <div className={styles.statCardVal}>{data.trips.length}</div>
                <div className={styles.statCardLabel}>Total Trips</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statCardVal}>{data.trips.filter(t => t.category === 'hiking').length}</div>
                <div className={styles.statCardLabel}>Hiking Trips</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statCardVal}>{data.trips.filter(t => t.thisWeek).length}</div>
                <div className={styles.statCardLabel}>This Week</div>
              </div>
            </div>
            <div className={styles.quickActions}>
              <h3 className={styles.sectionHead}>Quick Actions</h3>
              <div className={styles.quickGrid}>
                <button className={styles.quickBtn} onClick={() => { setTab('trips'); setTripModal('new'); }}>
                  <IconPlus size={20} /> Add New Trip
                </button>
                <button className={styles.quickBtn} onClick={() => setTab('hero')}>
                  <IconSettings size={20} /> Edit Hero
                </button>
                <button className={styles.quickBtn} onClick={() => navigate('/')}>
                  <IconEye size={20} /> Preview Site
                </button>
                {showSeedButton && (
                  <button className={styles.quickBtn} onClick={handleSeed} disabled={seeding} style={{ borderColor: '#e65100', color: '#e65100' }}>
                    {seeding ? '⏳ Seeding…' : '🔥 Seed Firestore'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === 'trips' && (
          <div className={styles.tabContent}>
            <div className={styles.tabHeader}>
              <h1 className={styles.pageTitle}>Manage Trips</h1>
              <button className={styles.addBtn} onClick={() => setTripModal('new')}>
                <IconPlus size={15} /> Add Trip
              </button>
            </div>
            <div className={styles.tableCard}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Location</th>
                    <th>Date</th>
                    <th>Fees</th>
                    <th>Category</th>
                    <th>This Week</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.trips.map(trip => {
                    const mainImg = trip.images?.find(i => i.isMain)?.url || trip.images?.[0]?.url || trip.image || '';
                    return (
                      <tr key={trip.id} className={trip.softDeleted ? styles.rowDeleted : ''}>
                        <td>
                          <div className={styles.tripRow}>
                            <img src={mainImg} alt="" className={styles.tripThumb} />
                            <div>
                              <span>{trip.title}</span>
                              {trip.images?.length > 1 && (
                                <span className={styles.photosBadge}>📷 {trip.images.length}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{trip.location}</td>
                        <td>{trip.date}</td>
                        <td><strong>{trip.fees}</strong></td>
                        <td>
                          <span className={`${styles.tag} ${styles[`tag_${trip.category}`]}`}>
                            {trip.category}
                          </span>
                        </td>
                        <td>
                          {trip.thisWeek
                            ? <span className={styles.thisWeekDot}>🗓 Yes</span>
                            : <span className={styles.notFeatured}>—</span>}
                        </td>
                        <td>
                          {trip.softDeleted
                            ? <span className={styles.deletedBadge}>Hidden</span>
                            : <span className={styles.activeBadge}>Active</span>}
                        </td>
                        <td>
                          <button className={`${styles.actionBtn} ${styles.editBtn}`} onClick={() => setTripModal(trip)}>
                            <IconEdit /> Edit
                          </button>
                          {trip.softDeleted ? (
                            <>
                              <button className={`${styles.actionBtn} ${styles.restoreBtn}`} onClick={() => restoreTrip(trip.id)}>
                                ↩ Restore
                              </button>
                              <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => { if (window.confirm('Permanently delete this trip?')) deleteTrip(trip.id); }}>
                                <IconTrash /> Delete
                              </button>
                            </>
                          ) : (
                            <button className={`${styles.actionBtn} ${styles.hideBtn}`} onClick={() => softDeleteTrip(trip.id)}>
                              👁 Hide
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {data.trips.length === 0 && (
                <div className={styles.emptyTable}>No trips yet. Add your first trip!</div>
              )}
            </div>
          </div>
        )}

        {/* ── CATEGORIES ── */}
        {tab === 'categories' && (
          <div className={styles.tabContent}>
            <h1 className={styles.pageTitle}>Categories</h1>
            <p className={styles.pageSub}>Manage trip categories — add, edit colours, or remove.</p>

            {/* Existing categories */}
            <div className={styles.catList}>
              {data.categories.map(cat => (
                <div key={cat.id} className={styles.catRow}>
                  {editingCat === cat.id ? (
                    <>
                      <span className={styles.catSwatch} style={{ background: catForm.bg, borderColor: catForm.color }} />
                      <input
                        className={styles.catLabelInput}
                        value={catForm.label}
                        onChange={e => setCatForm(f => ({ ...f, label: e.target.value }))}
                        placeholder="Label"
                      />
                      <div className={styles.catColorPair}>
                        <label className={styles.catColorLabel}>Text</label>
                        <input type="color" className={styles.catColorPicker} value={catForm.color}
                          onChange={e => setCatForm(f => ({ ...f, color: e.target.value }))} />
                        <label className={styles.catColorLabel}>Background</label>
                        <input type="color" className={styles.catColorPicker} value={catForm.bg}
                          onChange={e => setCatForm(f => ({ ...f, bg: e.target.value }))} />
                      </div>
                      <button className={`${styles.actionBtn} ${styles.saveBtn}`} style={{ padding: '6px 14px', fontSize: 13 }} onClick={() => {
                        if (!catForm.label.trim()) return;
                        updateCategories(data.categories.map(c => c.id === cat.id ? { ...c, ...catForm } : c));
                        setEditingCat(null);
                        showSaved('✅ Category updated!');
                      }}>Save</button>
                      <button className={styles.actionBtn} onClick={() => setEditingCat(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <span className={styles.catSwatch} style={{ background: cat.bg, borderColor: cat.color }} />
                      <span className={styles.catBadgePreview} style={{ background: cat.bg, color: cat.color }}>{cat.label}</span>
                      <span className={styles.catId}>id: {cat.id}</span>
                      <div className={styles.catUsage}>{data.trips.filter(t => t.category === cat.id).length} trips</div>
                      <button className={`${styles.actionBtn} ${styles.editBtn}`} onClick={() => {
                        setEditingCat(cat.id);
                        setCatForm({ label: cat.label, color: cat.color, bg: cat.bg });
                      }}><IconEdit size={13} /> Edit</button>
                      <button
                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                        disabled={data.categories.length <= 1}
                        title={data.categories.length <= 1 ? 'Must have at least 1 category' : `Delete ${cat.label}`}
                        onClick={() => {
                          const inUse = data.trips.filter(t => t.category === cat.id).length;
                          if (inUse > 0) {
                            alert(`Cannot delete "${cat.label}" — ${inUse} trip(s) use this category. Reassign them first.`);
                            return;
                          }
                          if (window.confirm(`Delete category "${cat.label}"?`)) {
                            updateCategories(data.categories.filter(c => c.id !== cat.id));
                            showSaved('✅ Category deleted!');
                          }
                        }}
                      ><IconTrash size={13} /> Delete</button>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Add new category */}
            <div className={styles.catAddCard}>
              <h3 className={styles.sectionHead}>Add New Category</h3>
              <div className={styles.catAddRow}>
                <div className={styles.formGroup} style={{ flex: 2 }}>
                  <label className={styles.label}>Label (display name)</label>
                  <input className={styles.input} value={catForm.label} onChange={e => setCatForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Swimming" />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>ID (slug, no spaces)</label>
                  <input className={styles.input}
                    value={catForm.label.toLowerCase().replace(/\s+/g, '-')}
                    readOnly
                    style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Text Color</label>
                  <input type="color" className={styles.catColorPickerLg} value={catForm.color}
                    onChange={e => setCatForm(f => ({ ...f, color: e.target.value }))} />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Background</label>
                  <input type="color" className={styles.catColorPickerLg} value={catForm.bg}
                    onChange={e => setCatForm(f => ({ ...f, bg: e.target.value }))} />
                </div>
              </div>
              <div className={styles.catPreviewRow}>
                <span className={styles.catPreviewLabel}>Preview:</span>
                <span className={styles.catBadgePreview} style={{ background: catForm.bg, color: catForm.color }}>
                  {catForm.label || 'Category Name'}
                </span>
              </div>
              <button
                className={styles.addBtn}
                disabled={!catForm.label.trim() || editingCat !== null}
                onClick={() => {
                  const id = catForm.label.trim().toLowerCase().replace(/\s+/g, '-');
                  if (data.categories.find(c => c.id === id)) {
                    alert(`A category with id "${id}" already exists.`);
                    return;
                  }
                  updateCategories([...data.categories, { id, label: catForm.label.trim(), color: catForm.color, bg: catForm.bg }]);
                  setCatForm({ label: '', color: '#2d7a3e', bg: '#e6f4ea' });
                  showSaved('✅ Category added!');
                }}
              >
                <IconPlus size={15} /> Add Category
              </button>
            </div>
          </div>
        )}

        {/* ── ABOUT ── */}
        {tab === 'about' && (
          <div className={styles.tabContent}>
            <h1 className={styles.pageTitle}>About Section</h1>
            <div className={styles.formCard}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Title (English)</label>
                <input className={styles.input} value={aboutForm.title} onChange={e => setAboutForm({ ...aboutForm, title: e.target.value })} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Body Text (English)</label>
                <textarea className={styles.textarea} value={aboutForm.body} onChange={e => setAboutForm({ ...aboutForm, body: e.target.value })} />
              </div>
              <h3 className={styles.sectionHead} style={{ marginTop: 24 }}>Stats</h3>
              {aboutForm.stats.map((s, i) => (
                <div key={i} className={styles.statsRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Label</label>
                    <input className={styles.input} value={s.label} onChange={e => {
                      const stats = [...aboutForm.stats]; stats[i] = { ...stats[i], label: e.target.value };
                      setAboutForm({ ...aboutForm, stats });
                    }} />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Value</label>
                    <input className={styles.input} value={s.value} onChange={e => {
                      const stats = [...aboutForm.stats]; stats[i] = { ...stats[i], value: e.target.value };
                      setAboutForm({ ...aboutForm, stats });
                    }} />
                  </div>
                </div>
              ))}
              <button className={styles.saveBtn} onClick={() => { updateAbout(aboutForm); showSaved('✅ About section saved!'); }}>
                Save Changes
              </button>
            </div>
          </div>
        )}

        {/* ── HERO ── */}
        {tab === 'hero' && (
          <div className={styles.tabContent}>
            <h1 className={styles.pageTitle}>Hero Section</h1>
            <div className={styles.formCard}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Badge Text</label>
                <input className={styles.input} value={heroForm.badge} onChange={e => setHeroForm({ ...heroForm, badge: e.target.value })} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Main Headline</label>
                <input className={styles.input} value={heroForm.headline} onChange={e => setHeroForm({ ...heroForm, headline: e.target.value })} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Sub-headline</label>
                <input className={styles.input} value={heroForm.subheadline} onChange={e => setHeroForm({ ...heroForm, subheadline: e.target.value })} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>CTA Button Text</label>
                <input className={styles.input} value={heroForm.cta} onChange={e => setHeroForm({ ...heroForm, cta: e.target.value })} />
              </div>
              <button className={styles.saveBtn} onClick={() => { updateHero(heroForm); showSaved('✅ Hero section saved!'); }}>
                Save Changes
              </button>
            </div>
          </div>
        )}

        {/* ── CORPORATE ── */}
        {tab === 'corporate' && (
          <div className={styles.tabContent}>
            <div className={styles.tabHeader}>
              <h1 className={styles.pageTitle}>Corporate Page</h1>
              <a href="/corporate" target="_blank" rel="noreferrer" className={styles.addBtn} style={{ textDecoration: 'none' }}>
                <IconEye size={15} /> View Page
              </a>
            </div>

            {/* ── Accordion: Content & Hero ── */}
            <div className={styles.accordion}>
              <button className={styles.accordionHead} onClick={() => setCorpAccordion(v => v === 'content' ? null : 'content')}>
                <span>🖼 Hero Image & Content</span>
                <span className={corpAccordion === 'content' ? styles.accordionArrowOpen : styles.accordionArrow}>▾</span>
              </button>
              {corpAccordion === 'content' && (
                <div className={styles.accordionBody}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Hero Background Image URL</label>
                    <input className={styles.input} value={corpForm.heroImage || ''} onChange={e => setCorpForm({ ...corpForm, heroImage: e.target.value })} placeholder="https://images.unsplash.com/..." />
                    {corpForm.heroImage && (
                      <img src={corpForm.heroImage} alt="Hero preview" className={styles.imgPreview} />
                    )}
                  </div>
                  <div className={styles.formGrid}>
                    <div className={`${styles.formGroup} ${styles.spanFull}`}>
                      <label className={styles.label}>Headline</label>
                      <input className={styles.input} value={corpForm.headline || ''} onChange={e => setCorpForm({ ...corpForm, headline: e.target.value })} placeholder="Team Building Experiences" />
                    </div>
                    <div className={`${styles.formGroup} ${styles.spanFull}`}>
                      <label className={styles.label}>Tagline</label>
                      <input className={styles.input} value={corpForm.tagline || ''} onChange={e => setCorpForm({ ...corpForm, tagline: e.target.value })} placeholder="Stronger teams. Smarter strategies." />
                    </div>
                    <div className={`${styles.formGroup} ${styles.spanFull}`}>
                      <label className={styles.label}>Description</label>
                      <textarea className={styles.textarea} rows={4} value={corpForm.description || ''} onChange={e => setCorpForm({ ...corpForm, description: e.target.value })} />
                    </div>
                  </div>
                  <button className={styles.saveBtn} onClick={() => {
                    updateCorporate({ ...data.corporate, heroImage: corpForm.heroImage, headline: corpForm.headline, tagline: corpForm.tagline, description: corpForm.description });
                    showSaved('✅ Corporate content saved!');
                  }}>Save Changes</button>
                </div>
              )}
            </div>

            {/* ── Accordion: Services ── */}
            <div className={styles.accordion}>
              <button className={styles.accordionHead} onClick={() => setCorpAccordion(v => v === 'services' ? null : 'services')}>
                <span>🎯 Services ({data.corporate.services.length})</span>
                <span className={corpAccordion === 'services' ? styles.accordionArrowOpen : styles.accordionArrow}>▾</span>
              </button>
              {corpAccordion === 'services' && (
                <div className={styles.accordionBody}>
                  <div className={styles.catList}>
                    {data.corporate.services.map(s => (
                      <div key={s.id} className={styles.catRow}>
                        {editingService === s.id ? (
                          <div style={{ flex: 1, display: 'grid', gap: 8 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: 8 }}>
                              <input className={styles.input} placeholder="Emoji" value={serviceForm.icon} onChange={e => setServiceForm(f => ({ ...f, icon: e.target.value }))} />
                              <input className={styles.input} placeholder="Title" value={serviceForm.title} onChange={e => setServiceForm(f => ({ ...f, title: e.target.value }))} />
                            </div>
                            <textarea className={styles.input} rows={2} value={serviceForm.body} onChange={e => setServiceForm(f => ({ ...f, body: e.target.value }))} style={{ resize: 'vertical' }} />
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button className={styles.saveBtn} style={{ padding: '6px 14px', fontSize: '0.82rem' }} onClick={() => {
                                const updated = data.corporate.services.map(sv => sv.id === s.id ? { ...sv, ...serviceForm } : sv);
                                updateCorporate({ ...data.corporate, services: updated });
                                setEditingService(null);
                                showSaved('✅ Service updated!');
                              }}>Save</button>
                              <button className={styles.cancelBtn} onClick={() => setEditingService(null)}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <span style={{ fontSize: '1.3rem', marginRight: 10 }}>{s.icon}</span>
                            <span style={{ flex: 1, fontWeight: 600 }}>{s.title}</span>
                            <button className={`${styles.actionBtn} ${styles.editBtn}`} onClick={() => { setEditingService(s.id); setServiceForm({ icon: s.icon, title: s.title, body: s.body }); }}><IconEdit size={13} /> Edit</button>
                            <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => {
                              if (data.corporate.services.length <= 1) return;
                              updateCorporate({ ...data.corporate, services: data.corporate.services.filter(sv => sv.id !== s.id) });
                              showSaved('✅ Service removed!');
                            }}><IconTrash size={13} /></button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className={styles.catAddCard}>
                    <h4 className={styles.sectionHead}>Add Service</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: 8, marginBottom: 8 }}>
                      <input className={styles.input} placeholder="Emoji" value={editingService ? '' : serviceForm.icon} onChange={e => setServiceForm(f => ({ ...f, icon: e.target.value }))} />
                      <input className={styles.input} placeholder="Title" value={editingService ? '' : serviceForm.title} onChange={e => setServiceForm(f => ({ ...f, title: e.target.value }))} />
                    </div>
                    <textarea className={styles.input} rows={2} placeholder="Description" value={editingService ? '' : serviceForm.body} onChange={e => setServiceForm(f => ({ ...f, body: e.target.value }))} style={{ resize: 'vertical', marginBottom: 12 }} />
                    <button className={styles.addBtn} onClick={() => {
                      if (!serviceForm.title.trim() || editingService) return;
                      const updated = [...data.corporate.services, { id: Date.now(), icon: serviceForm.icon || '🌿', title: serviceForm.title.trim(), body: serviceForm.body.trim() }];
                      updateCorporate({ ...data.corporate, services: updated });
                      setServiceForm({ icon: '', title: '', body: '' });
                      showSaved('✅ Service added!');
                    }}><IconPlus size={14} /> Add Service</button>
                  </div>
                </div>
              )}
            </div>

            {/* ── Accordion: Packages ── */}
            <div className={styles.accordion}>
              <button className={styles.accordionHead} onClick={() => setCorpAccordion(v => v === 'packages' ? null : 'packages')}>
                <span>📦 Packages ({(data.corporate.packages || []).length})</span>
                <span className={corpAccordion === 'packages' ? styles.accordionArrowOpen : styles.accordionArrow}>▾</span>
              </button>
              {corpAccordion === 'packages' && (
                <div className={styles.accordionBody}>
                  <div className={styles.catList}>
                    {(data.corporate.packages || []).map(pkg => (
                      <div key={pkg.id} className={styles.catRow} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                        {editingPkg === pkg.id ? (
                          <div style={{ width: '100%', display: 'grid', gap: 8 }}>
                            <div className={styles.formGrid}>
                              <div className={styles.formGroup}>
                                <label className={styles.label}>Title</label>
                                <input className={styles.input} value={pkgForm.title} onChange={e => setPkgForm(f => ({ ...f, title: e.target.value }))} />
                              </div>
                              <div className={styles.formGroup}>
                                <label className={styles.label}>Destination</label>
                                <input className={styles.input} value={pkgForm.destination} onChange={e => setPkgForm(f => ({ ...f, destination: e.target.value }))} />
                              </div>
                              <div className={styles.formGroup}>
                                <label className={styles.label}>Employees</label>
                                <input className={styles.input} value={pkgForm.employees} onChange={e => setPkgForm(f => ({ ...f, employees: e.target.value }))} placeholder="e.g. 20 – 50" />
                              </div>
                              <div className={styles.formGroup}>
                                <label className={styles.label}>Price</label>
                                <input className={styles.input} value={pkgForm.price} onChange={e => setPkgForm(f => ({ ...f, price: e.target.value }))} placeholder="e.g. 45 JD / person" />
                              </div>
                              <div className={`${styles.formGroup} ${styles.spanFull}`}>
                                <label className={styles.label}>Image URL</label>
                                <input className={styles.input} value={pkgForm.image} onChange={e => setPkgForm(f => ({ ...f, image: e.target.value }))} placeholder="https://..." />
                              </div>
                              <div className={`${styles.formGroup} ${styles.spanFull}`}>
                                <label className={styles.label}>Details</label>
                                <textarea className={styles.textarea} rows={3} value={pkgForm.details} onChange={e => setPkgForm(f => ({ ...f, details: e.target.value }))} />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button className={styles.saveBtn} style={{ padding: '6px 14px', fontSize: '0.82rem' }} onClick={() => {
                                const generateSlugHelper = (text) => 
                                  text
                                    .toLowerCase()
                                    .replace(/[^a-z0-9\s-]/g, '')
                                    .trim()
                                    .replace(/\s+/g, '-')
                                    .replace(/-+/g, '-');
                                const updated = data.corporate.packages.map(p => p.id === pkg.id ? { ...p, ...pkgForm, slug: generateSlugHelper(pkgForm.title) } : p);
                                updateCorporate({ ...data.corporate, packages: updated });
                                setEditingPkg(null);
                                showSaved('✅ Package updated!');
                              }}>Save</button>
                              <button className={styles.cancelBtn} onClick={() => setEditingPkg(null)}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontWeight: 700 }}>{pkg.title}</span>
                              <span style={{ marginLeft: 10, fontSize: '0.8rem', color: '#5b8f4b', background: '#e8f5e2', padding: '2px 10px', borderRadius: 12 }}>{pkg.price}</span>
                              <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 2 }}>{pkg.destination} · {pkg.employees} employees</div>
                            </div>
                            <button className={`${styles.actionBtn} ${styles.editBtn}`} onClick={() => { setEditingPkg(pkg.id); setPkgForm({ title: pkg.title, destination: pkg.destination, employees: pkg.employees, price: pkg.price, details: pkg.details, image: pkg.image || '' }); }}><IconEdit size={13} /> Edit</button>
                            <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => {
                              updateCorporate({ ...data.corporate, packages: data.corporate.packages.filter(p => p.id !== pkg.id) });
                              showSaved('✅ Package removed!');
                            }}><IconTrash size={13} /></button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className={styles.catAddCard}>
                    <h4 className={styles.sectionHead}>Add Package</h4>
                    <div className={styles.formGrid}>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Title</label>
                        <input className={styles.input} value={editingPkg ? '' : pkgForm.title} onChange={e => setPkgForm(f => ({ ...f, title: e.target.value }))} placeholder="Forest Team Day" />
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Destination</label>
                        <input className={styles.input} value={editingPkg ? '' : pkgForm.destination} onChange={e => setPkgForm(f => ({ ...f, destination: e.target.value }))} placeholder="Ajloun Forest" />
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Employees</label>
                        <input className={styles.input} value={editingPkg ? '' : pkgForm.employees} onChange={e => setPkgForm(f => ({ ...f, employees: e.target.value }))} placeholder="20 – 50" />
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Price</label>
                        <input className={styles.input} value={editingPkg ? '' : pkgForm.price} onChange={e => setPkgForm(f => ({ ...f, price: e.target.value }))} placeholder="45 JD / person" />
                      </div>
                      <div className={`${styles.formGroup} ${styles.spanFull}`}>
                        <label className={styles.label}>Image URL</label>
                        <input className={styles.input} value={editingPkg ? '' : pkgForm.image} onChange={e => setPkgForm(f => ({ ...f, image: e.target.value }))} placeholder="https://..." />
                      </div>
                      <div className={`${styles.formGroup} ${styles.spanFull}`}>
                        <label className={styles.label}>Details</label>
                        <textarea className={styles.textarea} rows={3} value={editingPkg ? '' : pkgForm.details} onChange={e => setPkgForm(f => ({ ...f, details: e.target.value }))} placeholder="Describe the package..." />
                      </div>
                    </div>
                    <button className={styles.addBtn} onClick={() => {
                      if (!pkgForm.title.trim() || editingPkg) return;
                      const generateSlugHelper = (text) => 
                        text
                          .toLowerCase()
                          .replace(/[^a-z0-9\s-]/g, '')
                          .trim()
                          .replace(/\s+/g, '-')
                          .replace(/-+/g, '-');
                      const updated = [...(data.corporate.packages || []), { id: Date.now(), slug: generateSlugHelper(pkgForm.title), ...pkgForm }];
                      updateCorporate({ ...data.corporate, packages: updated });
                      setPkgForm({ title: '', destination: '', employees: '', price: '', details: '', image: '' });
                      showSaved('✅ Package added!');
                    }}><IconPlus size={14} /> Add Package</button>
                  </div>
                </div>
              )}
            </div>

            {/* ── Accordion: Clients ── */}
            <div className={styles.accordion}>
              <button className={styles.accordionHead} onClick={() => setCorpAccordion(v => v === 'clients' ? null : 'clients')}>
                <span>🏢 Our Clients ({data.corporate.clients.length})</span>
                <span className={corpAccordion === 'clients' ? styles.accordionArrowOpen : styles.accordionArrow}>▾</span>
              </button>
              {corpAccordion === 'clients' && (
                <div className={styles.accordionBody}>
                  <div className={styles.galleryPreviewGrid} style={{ marginBottom: 20 }}>
                    {data.corporate.clients.map(client => (
                      <div key={client.id} className={styles.galleryPreviewItem} style={{ position: 'relative' }}>
                        <img src={client.logo} alt={client.name} style={{ height: 50, objectFit: 'contain', background: '#fff', padding: 8 }} />
                        <div className={styles.galleryPreviewLabel}>{client.name}</div>
                        <button
                          style={{ position: 'absolute', top: 4, right: 4, background: '#fff', border: '1px solid #e0d9ce', borderRadius: 6, padding: '2px 6px', fontSize: 11, cursor: 'pointer', color: '#d04040' }}
                          onClick={() => {
                            updateCorporate({ ...data.corporate, clients: data.corporate.clients.filter(c => c.id !== client.id) });
                            showSaved('✅ Client removed!');
                          }}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                  <div className={styles.catAddCard}>
                    <h4 className={styles.sectionHead}>Add Client</h4>
                    <div className={styles.formGrid}>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Company Name</label>
                        <input className={styles.input} placeholder="e.g. Orange Jordan" value={newClient.name} onChange={e => setNewClient(c => ({ ...c, name: e.target.value }))} />
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.label}>Logo URL</label>
                        <input className={styles.input} placeholder="https://..." value={newClient.logo} onChange={e => setNewClient(c => ({ ...c, logo: e.target.value }))} />
                      </div>
                    </div>
                    <button className={styles.addBtn} onClick={() => {
                      if (!newClient.name.trim()) return;
                      const updated = [...data.corporate.clients, { id: Date.now(), name: newClient.name.trim(), logo: newClient.logo.trim() }];
                      updateCorporate({ ...data.corporate, clients: updated });
                      setNewClient({ name: '', logo: '' });
                      showSaved('✅ Client added!');
                    }}><IconPlus size={14} /> Add Client</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'contact' && (
          <div className={styles.tabContent}>
            <h1 className={styles.pageTitle}>Contact & Social Media</h1>
            <div className={styles.formCard}>
              <h3 className={styles.sectionHead}>Contact Information</h3>
              <div className={styles.formGroup}>
                <label className={styles.label}>Phone Number</label>
                <input className={styles.input} type="tel" value={contactForm.phone || ''} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="+962 77 611 3530" />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Email</label>
                <input className={styles.input} type="email" value={contactForm.email || ''} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} placeholder="experience@keepersoftheforest.com" />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Location</label>
                <input className={styles.input} value={contactForm.location || ''} onChange={e => setContactForm({ ...contactForm, location: e.target.value })} placeholder="Amman, Jordan" />
              </div>

              <h3 className={styles.sectionHead} style={{ marginTop: 24 }}>Social Media</h3>
              <div className={styles.socialRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Instagram Handle</label>
                  <input className={styles.input} value={contactForm.instagram || ''} onChange={e => setContactForm({ ...contactForm, instagram: e.target.value })} placeholder="@keepers.experience" />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Instagram Profile URL</label>
                  <input className={styles.input} value={contactForm.instagramUrl || ''} onChange={e => setContactForm({ ...contactForm, instagramUrl: e.target.value })} placeholder="https://www.instagram.com/keepers.experience/" />
                </div>
              </div>

              <div className={styles.socialRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Facebook Page Name</label>
                  <input className={styles.input} value={contactForm.facebook || ''} onChange={e => setContactForm({ ...contactForm, facebook: e.target.value })} placeholder="Keepers Experience Jordan" />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Facebook Page URL</label>
                  <input className={styles.input} value={contactForm.facebookUrl || ''} onChange={e => setContactForm({ ...contactForm, facebookUrl: e.target.value })} placeholder="https://www.facebook.com/keepers.experience/" />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>WhatsApp Number</label>
                <input className={styles.input} type="tel" value={contactForm.whatsapp || ''} onChange={e => setContactForm({ ...contactForm, whatsapp: e.target.value })} placeholder="+962 79 000 0000" />
              </div>

              <button className={styles.saveBtn} onClick={() => { updateContact(contactForm); showSaved('✅ Contact information saved!'); }}>
                Save Changes
              </button>
            </div>
          </div>
        )}

        {tab === 'privacy' && (
          <div className={styles.tabContent}>
            <h1 className={styles.pageTitle}>Privacy Policy</h1>
            <p style={{ color: '#888', marginBottom: 24, fontSize: '0.9rem' }}>Edit the sections that appear in the Privacy Policy popup on your website.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {privacyForm.sections.map((section, i) => (
                <div key={section.id} className={styles.formCard} style={{ padding: '20px 24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#b8d4b2', minWidth: 24 }}>{String(i + 1).padStart(2, '0')}</span>
                    <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#14231a' }}>{section.title}</h4>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Section Title</label>
                    <input
                      className={styles.input}
                      value={section.title}
                      onChange={e => {
                        const updated = privacyForm.sections.map(s => s.id === section.id ? { ...s, title: e.target.value } : s);
                        setPrivacyForm({ ...privacyForm, sections: updated });
                      }}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Content</label>
                    <textarea
                      className={styles.textarea}
                      rows={4}
                      value={section.body}
                      onChange={e => {
                        const updated = privacyForm.sections.map(s => s.id === section.id ? { ...s, body: e.target.value } : s);
                        setPrivacyForm({ ...privacyForm, sections: updated });
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.formGroup} style={{ marginTop: 12 }}>
              <label className={styles.label}>Last Updated</label>
              <input
                className={styles.input}
                value={privacyForm.lastUpdated || ''}
                onChange={e => setPrivacyForm({ ...privacyForm, lastUpdated: e.target.value })}
                placeholder="e.g. April 2026"
              />
            </div>
            <button
              className={styles.saveBtn}
              onClick={() => { updatePrivacyPolicy(privacyForm); showSaved('✅ Privacy Policy saved!'); }}
            >
              Save Changes
            </button>
          </div>
        )}

        {tab === 'blog' && (
          <div className={styles.tabContent}>
            <div className={styles.sectionTop} style={{ marginBottom: 24 }}>
              <h1 className={styles.pageTitle} style={{ margin: 0 }}>Blog Posts</h1>
              <button className={styles.addBtn} onClick={() => { setBlogForm(emptyPost); setBlogDirty(false); setBlogModal('new'); }}>
                <IconPlus size={15} /> New Post
              </button>
            </div>

            {(data.posts || []).length === 0 ? (
              <p style={{ color: '#aaa', fontSize: '0.9rem' }}>No posts yet. Click "New Post" to create one.</p>
            ) : (
              <div className={styles.tripsTable}>
                <div className={styles.tableHead}>
                  <span>Title</span>
                  <span>Category</span>
                  <span>Date</span>
                  <span>Status</span>
                  <span>Actions</span>
                </div>
                {(data.posts || []).map(post => (
                  <div key={post.id} className={styles.tableRow}>
                    <span className={styles.tripTitle}>{post.title}</span>
                    <span style={{ fontSize: '0.8rem', color: '#666', textTransform: 'capitalize' }}>{post.category}</span>
                    <span style={{ fontSize: '0.8rem', color: '#888' }}>{post.date}</span>
                    <span>
                      <span style={{
                        fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                        background: post.published ? '#e6f4ea' : '#f5f5f5',
                        color: post.published ? '#2d7a3e' : '#999',
                      }}>
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                    </span>
                    <div className={styles.tableActions}>
                      <button className={`${styles.actionBtn} ${styles.editBtn}`} onClick={() => { setBlogForm(post); setBlogDirty(false); setBlogModal(post); }}>
                        <IconEdit size={13} /> Edit
                      </button>
                      <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => { if (window.confirm('Delete this post?')) deletePost(post.id); }}>
                        <IconTrash size={13} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'events' && (
          <div className={styles.tabContent}>
            <div className={styles.sectionTop} style={{ marginBottom: 24 }}>
              <h1 className={styles.pageTitle} style={{ margin: 0 }}>Experiences</h1>
              <button className={styles.addBtn} onClick={() => { setEventForm(emptyEvent); setEventDirty(false); setEventModal('new'); }}>
                <IconPlus size={15} /> New Experience
              </button>
            </div>

            {(data.events || []).length === 0 ? (
              <p style={{ color: '#aaa', fontSize: '0.9rem' }}>No experiences yet. Click "New Experience" to create one.</p>
            ) : (
              <div className={styles.tripsTable}>
                <div className={styles.tableHead}>
                  <span>Title</span>
                  <span>Location</span>
                  <span>Date</span>
                  <span>Upcoming</span>
                  <span>Actions</span>
                </div>
                {(data.events || []).map(event => (
                  <div key={event.id} className={styles.tableRow}>
                    <span className={styles.tripTitle}>{event.title}</span>
                    <span style={{ fontSize: '0.8rem', color: '#666' }}>{event.location}</span>
                    <span style={{ fontSize: '0.8rem', color: '#888' }}>{event.date}</span>
                    <span>
                      <span style={{
                        fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                        background: event.upcoming ? '#e8f4ff' : '#f5f5f5',
                        color: event.upcoming ? '#1a56c4' : '#999',
                      }}>
                        {event.upcoming ? 'Upcoming' : '-'}
                      </span>
                    </span>
                    <div className={styles.tableActions}>
                      <button className={`${styles.actionBtn} ${styles.editBtn}`} onClick={() => { setEventForm({ ...event, tags: (event.tags || []).join(', ') }); setEventDirty(false); setEventModal(event); }}>
                        <IconEdit size={13} /> Edit
                      </button>
                      <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => { if (window.confirm('Delete this event?')) deleteEvent(event.id); }}>
                        <IconTrash size={13} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </main>

      {/* BLOG MODAL */}
      {blogModal && (
        <div className={styles.modalOverlay} onClick={handleCloseBlog}>
          <div className={styles.modal} onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>{blogModal === 'new' ? 'New Post' : 'Edit Post'}</h2>
              <button className={styles.modalClose} onClick={handleCloseBlog}>✕</button>
            </div>
            <div className={styles.modalBody}>

              <div className={styles.formGroup}>
                <label className={styles.label}>Title *</label>
                <input className={styles.input} value={blogForm.title} onChange={e => patchBlogForm(f => ({ ...f, title: e.target.value }))} placeholder="Post title" />
              </div>

              <div className={styles.formRow}>
                {/* <div className={styles.formGroup}>
                  <label className={styles.label}>Slug (Auto-generated)</label>
                  <div className={styles.input} style={{ background: '#f5f5f5', color: '#666', display: 'flex', alignItems: 'center', paddingRight: 12 }}>
                    {(blogForm.title || 'your-title').toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-')}
                  </div>
                </div> */}
                <div className={styles.formGroup}>
                  <label className={styles.label}>Category</label>
                  <select className={styles.input} value={blogForm.category} onChange={e => patchBlogForm(f => ({ ...f, category: e.target.value }))}>
                    <option value="trip-report">Trip Report</option>
                    <option value="tips">Tips & Guides</option>
                    <option value="news">News</option>
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Date</label>
                  <input className={styles.input} value={blogForm.date} onChange={e => patchBlogForm(f => ({ ...f, date: e.target.value }))} placeholder="e.g. April 2026" />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Author</label>
                  <input className={styles.input} value={blogForm.author} onChange={e => patchBlogForm(f => ({ ...f, author: e.target.value }))} />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Cover Image URL</label>
                <input className={styles.input} value={blogForm.coverImage} onChange={e => patchBlogForm(f => ({ ...f, coverImage: e.target.value }))} placeholder="https://..." />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Excerpt (short description)</label>
                <textarea className={styles.textarea} rows={2} value={blogForm.excerpt} onChange={e => patchBlogForm(f => ({ ...f, excerpt: e.target.value }))} placeholder="One-sentence summary..." />
              </div>

              <div className={styles.formGroup} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="postPublished" checked={blogForm.published} onChange={e => patchBlogForm(f => ({ ...f, published: e.target.checked }))} />
                <label htmlFor="postPublished" className={styles.label} style={{ margin: 0, cursor: 'pointer' }}>Published (visible on website)</label>
              </div>

              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <label className={styles.label} style={{ margin: 0 }}>Sections</label>
                  <button
                    className={styles.addBtn}
                    style={{ fontSize: '0.8rem', padding: '4px 14px' }}
                    onClick={() => patchBlogForm(f => ({ ...f, sections: [...f.sections, { id: Date.now(), heading: '', body: '' }] }))}
                  >
                    <IconPlus size={13} /> Add Section
                  </button>
                </div>
                {blogForm.sections.map((sec, i) => (
                  <div key={sec.id} style={{ background: '#f8f9fa', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#aaa' }}>{String(i + 1).padStart(2, '0')}</span>
                      <input
                        className={styles.input}
                        style={{ flex: 1 }}
                        value={sec.heading}
                        onChange={e => patchBlogForm(f => ({ ...f, sections: f.sections.map(s => s.id === sec.id ? { ...s, heading: e.target.value } : s) }))}
                        placeholder="Section heading (optional)"
                      />
                      {blogForm.sections.length > 1 && (
                        <button
                          className={styles.deleteBtn}
                          style={{ padding: '4px 10px' }}
                          onClick={() => patchBlogForm(f => ({ ...f, sections: f.sections.filter(s => s.id !== sec.id) }))}
                        >
                          <IconTrash size={12} />
                        </button>
                      )}
                    </div>
                    <textarea
                      className={styles.textarea}
                      rows={3}
                      value={sec.body}
                      onChange={e => patchBlogForm(f => ({ ...f, sections: f.sections.map(s => s.id === sec.id ? { ...s, body: e.target.value } : s) }))}
                      placeholder="Paragraph text..."
                    />
                  </div>
                ))}
              </div>

            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={handleCloseBlog}>Cancel</button>
              <button
                className={styles.saveBtn}
                onClick={() => {
                  if (!blogForm.title.trim()) return;
                  const slug = blogForm.slug || blogForm.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                  const post = { ...blogForm, slug };
                  if (blogModal === 'new') addPost(post);
                  else updatePost(post);
                  setBlogModal(null);
                  setBlogDirty(false);
                  showSaved('✅ Post saved!');
                }}
              >
                Save Post
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EVENT MODAL */}
      {eventModal && (
        <div className={styles.modalOverlay} onClick={handleCloseEvent}>
          <div className={styles.modal} onClick={e => e.stopPropagation()} style={{ maxWidth: 620 }}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>{eventModal === 'new' ? 'New Experience' : 'Edit Experience'}</h2>
              <button className={styles.modalClose} onClick={handleCloseEvent}>✕</button>
            </div>
            <div className={styles.modalBody}>

              <div className={styles.formGroup}>
                <label className={styles.label}>Title *</label>
                <input className={styles.input} value={eventForm.title} onChange={e => patchEventForm(f => ({ ...f, title: e.target.value }))} placeholder="Event title" />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Location</label>
                  <input className={styles.input} value={eventForm.location} onChange={e => patchEventForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Wadi Rum" />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Group Size</label>
                  <input className={styles.input} value={eventForm.groupSize} onChange={e => patchEventForm(f => ({ ...f, groupSize: e.target.value }))} placeholder="e.g. 10–40 people" />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Date</label>
                  <input className={styles.input} value={eventForm.date} onChange={e => patchEventForm(f => ({ ...f, date: e.target.value }))} placeholder="e.g. Friday 10/04" />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Time</label>
                  <input className={styles.input} value={eventForm.time} onChange={e => patchEventForm(f => ({ ...f, time: e.target.value }))} placeholder="e.g. 8:00 PM" />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Fees</label>
                <input className={styles.input} value={eventForm.fees} onChange={e => patchEventForm(f => ({ ...f, fees: e.target.value }))} placeholder="e.g. 35 JDs" />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Image URL</label>
                <input className={styles.input} value={eventForm.image} onChange={e => patchEventForm(f => ({ ...f, image: e.target.value }))} placeholder="https://..." />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Description</label>
                <textarea className={styles.textarea} rows={3} value={eventForm.description} onChange={e => patchEventForm(f => ({ ...f, description: e.target.value }))} placeholder="Event description..." />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Tags (comma-separated)</label>
                <input className={styles.input} value={eventForm.tags} onChange={e => patchEventForm(f => ({ ...f, tags: e.target.value }))} placeholder="e.g. Desert, Stargazing, Team Games" />
              </div>

              <div style={{ display: 'flex', gap: 24 }}>
                <div className={styles.formGroup} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <input type="checkbox" id="evtUpcoming" checked={!!eventForm.upcoming} onChange={e => patchEventForm(f => ({ ...f, upcoming: e.target.checked }))} />
                  <label htmlFor="evtUpcoming" className={styles.label} style={{ margin: 0, cursor: 'pointer' }}>Upcoming</label>
                </div>
                <div className={styles.formGroup} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <input type="checkbox" id="evtPublished" checked={eventForm.published} onChange={e => patchEventForm(f => ({ ...f, published: e.target.checked }))} />
                  <label htmlFor="evtPublished" className={styles.label} style={{ margin: 0, cursor: 'pointer' }}>Published</label>
                </div>
              </div>

            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={handleCloseEvent}>Cancel</button>
              <button
                className={styles.saveBtn}
                onClick={() => {
                  if (!eventForm.title.trim()) return;
                  const tags = eventForm.tags
                    ? eventForm.tags.split(',').map(t => t.trim()).filter(Boolean)
                    : [];
                  const ev = { ...eventForm, tags, upcoming: !!eventForm.upcoming, featured: undefined };
                  if (eventModal === 'new') addEvent(ev);
                  else updateEvent(ev);
                  setEventModal(null);
                  setEventDirty(false);
                  showSaved('✅ Event saved!');
                }}
              >
                Save Event
              </button>
            </div>
          </div>
        </div>
      )}

      {(tripModal === 'new' || (tripModal && tripModal !== 'new')) && (
        <TripModal
          key={tripModal === 'new' ? 'new' : tripModal.id}
          trip={tripModal === 'new' ? null : tripModal}
          onSave={handleSaveTrip}
          onClose={() => setTripModal(null)}
          categories={data.categories}
        />
      )}
    </div>
    </>
  );
}
